import React from 'react';
import type { EvidenceItem } from '../../types/evidence';
import EvidenceItemCard from './EvidenceItemCard';
import { FileX, Upload } from 'lucide-react';

interface EvidenceListProps {
  items: EvidenceItem[];
  onItemClick: (item: EvidenceItem) => void;
  onUpload?: () => void;
  isLoading?: boolean;
}

const EvidenceList: React.FC<EvidenceListProps> = ({
  items,
  onItemClick,
  onUpload,
  isLoading = false
}) => {
  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, idx) => (
          <div key={idx} className="bg-white border border-gray-200 rounded-lg p-4 animate-pulse">
            <div className="flex items-start gap-3">
              <div className="h-5 w-5 bg-gray-200 rounded"></div>
              <div className="flex-1 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                <div className="flex gap-2">
                  <div className="h-6 w-20 bg-gray-200 rounded"></div>
                  <div className="h-6 w-20 bg-gray-200 rounded"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <FileX className="h-16 w-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Evidence Items Found
        </h3>
        <p className="text-gray-600 mb-6 max-w-md mx-auto">
          No evidence has been collected yet. Sync your evidence sources or upload evidence manually.
        </p>
        {onUpload && (
          <button
            onClick={onUpload}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Upload className="h-5 w-5" />
            Upload Evidence
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {items.map((item) => (
        <EvidenceItemCard
          key={item.id}
          evidence={item}
          onClick={onItemClick}
        />
      ))}

      {items.length > 0 && (
        <div className="text-center pt-4">
          <p className="text-sm text-gray-600">
            Showing {items.length} evidence item{items.length !== 1 ? 's' : ''}
          </p>
        </div>
      )}
    </div>
  );
};

export default EvidenceList;
